import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOSchema from '../../components/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goautomat.com';

export default function PrivacyEn() {
  useEffect(() => {
    document.title = 'Privacy Policy | Automat';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Automat Privacy Policy. Learn how we collect, use, and protect your personal data in compliance with the LGPD.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'privacy policy, LGPD, data protection, automat privacy');
    }

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy",
    "description": "Automat Privacy Policy. Learn how we collect, use, and protect your personal data.",
    "url": `${siteUrl}/privacy`
  };

  return (
    <div className="bg-gray-950 min-h-screen">
      <SEOSchema schema={privacySchema} />

      {/* Navigation */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center cursor-pointer">
              <img src="/assets/media/automat-logo.png" alt="Automat Logo" className="h-10 w-auto" />
            </Link>
            <Link to="/" className="text-gray-400 hover:text-purple-400 transition-colors duration-300 cursor-pointer">
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          PRIVACY POLICY — AUTOMAT
        </h1>

        <p className="text-gray-400 mb-4">Last updated: June 11, 2026</p>

        <p className="text-gray-500 text-sm mb-12 italic">
          Versão em português disponível em{' '}
          <Link to="/privacidade" className="text-purple-400 hover:text-purple-300">
            goautomat.com/privacidade
          </Link>
          .
        </p>

        <div className="space-y-8 text-gray-300">
          <section>
            <p className="mb-6">
              AUTOMAT TECNOLOGIA DA INFORMAÇÃO LTDA ("AUTOMAT") is committed to the security and privacy of the data it processes. This Policy describes how we operate our customer-engagement platform (SaaS) in compliance with the Brazilian General Data Protection Law (LGPD) and with the terms of the WhatsApp Business Platform provided by Meta Platforms, Inc.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. DEFINITIONS AND ROLES</h2>
            <div className="space-y-3">
              <p>
                <strong className="text-white">You (the CUSTOMER):</strong> You are the Data Controller. You are responsible for the data of your end customers that you collect and enter into the platform.
              </p>
              <p>
                <strong className="text-white">AUTOMAT:</strong> We act as the Data Processor. We provide the technology to process this information according to your instructions.
              </p>
              <p>
                <strong className="text-white">Meta Platforms, Inc.:</strong> Provider of the WhatsApp Business Platform (Cloud API), through which messages are transmitted. Meta acts as a sub-processor within the WhatsApp messaging flow and processes data in accordance with its own policies.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. INFORMATION WE PROCESS</h2>
            <p className="mb-4">To operate the service, we process:</p>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-3">A. Your Account Data:</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Registration data (company name, contact name, email, phone, tax IDs) for billing, invoicing, and system access.</li>
                <li>Access logs and technical connection data for account security.</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-3">B. Third-Party Data (Your Customers/Leads):</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>History of text, audio, image, and file messages exchanged through the platform.</li>
                <li>Public WhatsApp profile data (name and photo) of users who interact with your connected number.</li>
                <li>Phone numbers and conversation metadata (date, time, and delivery status of messages) of contacts who interact with your number.</li>
                <li>
                  <strong className="text-white">Imported conversation history:</strong> when you connect your number in Coexistence mode, the WhatsApp Business Platform may provide up to six (6) months of prior message history, which is imported into the platform to ensure continuity of service.
                </li>
                <li>
                  <strong className="text-white">Messages sent by the CUSTOMER through the app:</strong> in Coexistence mode, messages that you or your team send directly from the WhatsApp Business app on your devices are synchronized with the platform (through the message echo events provided by Meta), in order to keep a unified, up-to-date inbox.
                </li>
                <li>Information entered manually by your team into the CRM (labels, notes, pipelines).</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. INTEGRATION AND USE OF INFORMATION</h2>
            <p className="mb-4">
              AUTOMAT integrates with the <strong className="text-white">WhatsApp Business Platform (Cloud API)</strong>, provided by Meta Platforms, Inc., acting as an authorized <strong className="text-white">Tech Provider</strong>.
            </p>
            <p className="mb-4">
              The CUSTOMER's number is connected exclusively through the official <strong className="text-white">Embedded Signup</strong> flow, including the <strong className="text-white">Coexistence</strong> mode, in which the CUSTOMER connects their number to the platform while continuing to use the WhatsApp Business app on their device. <strong className="text-white">We do not use any unofficial access, emulation, mirroring, or unauthorized automation of the WhatsApp application.</strong>
            </p>
            <p className="mb-4">We use the information strictly to:</p>
            <ul className="list-disc list-inside space-y-3 ml-4">
              <li>
                <strong className="text-white">Connectivity:</strong> establish and maintain the connection between the CUSTOMER's number and the management platform through Meta's official API.
              </li>
              <li>
                <strong className="text-white">Customer service:</strong> centralize and organize conversations in a unified inbox, allowing your team to respond to and manage interactions.
              </li>
              <li>
                <strong className="text-white">Automation and Artificial Intelligence:</strong> process incoming messages and generate automated responses, including through Artificial Intelligence, according to the flows configured by the CUSTOMER. For this purpose, message content may be transmitted to AI model providers contracted by AUTOMAT, solely to generate the requested response or execute the requested automation, under confidentiality obligations and without such providers using the content to train their models.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. SHARING AND STORAGE</h2>
            <ul className="list-disc list-inside space-y-3 ml-4">
              <li>Data is stored on secure servers, with encryption in transit and at rest.</li>
              <li><strong className="text-white">We do not sell, rent, or share your data with third parties for commercial or advertising purposes.</strong></li>
              <li>
                We may share information only with service providers (sub-processors) essential to delivering the service, under strict confidentiality agreements, including:
                <ul className="list-disc list-inside space-y-2 ml-6 mt-2">
                  <li><strong className="text-white">Meta Platforms, Inc.</strong> — transmission of messages via the WhatsApp Business Platform;</li>
                  <li><strong className="text-white">Supabase</strong> — database and storage infrastructure;</li>
                  <li><strong className="text-white">Vercel</strong> — application hosting;</li>
                  <li><strong className="text-white">Artificial Intelligence providers</strong> — processing of messages to generate automated responses, when enabled by the CUSTOMER.</li>
                </ul>
              </li>
              <li>Data processing carried out through the WhatsApp Business Platform complies with Meta's Terms and applicable platform policies.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. SECURITY AND PROTECTION</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We implement technical and organizational measures to protect data against unauthorized access, leaks, or loss.</li>
              <li>We use authentication, SSL/TLS encryption, integrity validation of incoming requests (signature verification), and continuous security monitoring.</li>
              <li>We perform regular backups to ensure service continuity.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. YOUR RIGHTS (LGPD)</h2>
            <p className="mb-4">
              As the Controller of your customers' data, you are responsible for ensuring their rights. As the holder of your AUTOMAT account data, you may:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Access, correct, or delete your registration data.</li>
              <li>Request data portability.</li>
              <li>Withdraw consent at any time.</li>
              <li>Request the complete deletion of your account and associated data.</li>
            </ul>
            <p>
              To exercise these rights, contact us at:{' '}
              <strong className="text-white">contato@goautomat.com</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. DATA RETENTION</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We retain data for the duration of the contract.</li>
              <li>After cancellation, data may be retained for up to five (5) years for auditing, legal compliance, and dispute resolution purposes.</li>
              <li>You may request early deletion, except where there is a legal obligation to retain the data.</li>
              <li>Upon a deletion request, message and contact data associated with the account is removed from our active systems within a reasonable period, subject to legal retention obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. COOKIES AND TRACKING</h2>
            <p>
              We use essential cookies for the platform to function (authentication, session) and analytics cookies to improve the user experience. You can manage your cookie preferences in your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. CHANGES TO THIS POLICY</h2>
            <p>
              We may update this Policy periodically. Significant changes will be communicated by email or through a notice on the platform. The date of the last update will always be indicated at the top of this document.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. CUSTOMER RESPONSIBILITIES</h2>
            <p className="mb-4">As the Controller, you are responsible for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Obtaining valid consent from your end customers for the collection and processing of data.</li>
              <li>Informing your customers about how their data will be used.</li>
              <li>Ensuring that only necessary data is entered into the platform.</li>
              <li>Responding to data subject rights requests (from your customers).</li>
              <li>Using the platform in compliance with Meta's Terms and WhatsApp Business policies, including messaging and acceptable use policies.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. GOVERNING LAW</h2>
            <p className="mb-4">This Policy is governed by Brazilian law, in particular:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Law No. 13.709/2018 (LGPD)</li>
              <li>Brazilian Internet Civil Framework (Law No. 12.965/2014)</li>
              <li>Consumer Protection Code (Law No. 8.078/1990)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. CONTACT AND DATA PROTECTION OFFICER</h2>
            <p className="mb-4">For questions, requests, or to exercise privacy-related rights:</p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <ul className="list-none space-y-3">
                <li><strong className="text-white">Legal Entity:</strong> AUTOMAT TECNOLOGIA DA INFORMAÇÃO LTDA</li>
                <li><strong className="text-white">Tax ID (CNPJ):</strong> 59.640.197/0001-76</li>
                <li><strong className="text-white">Email:</strong> contato@goautomat.com</li>
                <li><strong className="text-white">Website:</strong> goautomat.com</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. CONSENT</h2>
            <p>
              By using the AUTOMAT platform, you declare that you have read, understood, and agreed to the terms of this Privacy Policy.
            </p>
          </section>

          <div className="pt-8 border-t border-gray-800 text-sm text-gray-500">
            <p className="mb-2">Last updated: June 11, 2026. Document valid and effective as of this date.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>© 2026 Automat. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
